import React, { useState } from 'react';
import DashboardMetrics from './DashboardMetrics';
import ChatInterface from './ChatInterface';
import DateRangePicker from './DateRangePicker';
import ModeExplainerModal from './ModeExplainerModal';
import { TimeRange } from '../../types/analytics';
import { useProjectConfig } from '../../hooks/useProjectConfig';

const ExecutiveDashboard: React.FC = () => {
  const projectConfig = useProjectConfig();
  const [activeTab, setActiveTab] = useState<'metrics' | 'chat'>('metrics');
  const [analyticsMode, setAnalyticsMode] = useState<'hive' | 'iceberg'>('hive');
  const [showModeExplainer, setShowModeExplainer] = useState(false);
  const [timeTravelTimestamp, setTimeTravelTimestamp] = useState<string | null>(null);

  // Initialize with today's date
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const [timeRange, setTimeRange] = useState<TimeRange>({
    startDate: todayStr,
    endDate: todayStr,
    preset: 'today',
  });

  // API base URL - should match the deployed API Gateway endpoint
  const API_BASE_URL = 'https://a8jboiv7a5.execute-api.us-east-1.amazonaws.com';

  // Get project slug for analytics API calls (maps to Lambda's project parameter)
  // PVQ.ai slug 'pvq-ai' -> Lambda project 'pvq'
  // PDT slug 'pdt-project' -> Lambda project 'pdt'
  const projectSlug = projectConfig.slug === 'pvq-ai' ? 'pvq' : 'pdt';

  return (
    <div className="flex-1 bg-gray-900 text-gray-100 min-h-screen">
      <div className="h-full px-4 py-6">
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-100">
                {projectConfig.name} Executive Analytics Dashboard
              </h1>
              <p className="text-gray-400 mt-2">
                {projectConfig.slug === 'pvq-ai'
                  ? 'NOC Monitoring Analytics with Natural Language Query'
                  : 'Position Designation Tool Analytics'}
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* Analytics Mode Toggle - PVQ only (PDT doesn't have Iceberg pipeline) */}
              {projectSlug === 'pvq' && (
                <>
                  <div className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 font-medium">Data Source:</span>
                      <select
                        value={analyticsMode}
                        onChange={(e) => setAnalyticsMode(e.target.value as 'hive' | 'iceberg')}
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-sm text-gray-200 focus:outline-none focus:border-blue-500 transition-colors"
                      >
                        <option value="hive">Hive (Production)</option>
                        <option value="iceberg">Iceberg (Beta)</option>
                      </select>
                      {/* Info Icon */}
                      <button
                        onClick={() => setShowModeExplainer(true)}
                        className="text-gray-400 hover:text-blue-400 transition-colors"
                        title="Learn about data source options"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Time Travel UI (Iceberg only) */}
                  {analyticsMode === 'iceberg' && (
                    <div className="bg-purple-900 bg-opacity-30 border border-purple-700 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-purple-400 font-medium">⏰ Time Travel:</span>
                        <input
                          type="datetime-local"
                          value={timeTravelTimestamp || ''}
                          onChange={(e) => setTimeTravelTimestamp(e.target.value || null)}
                          className="bg-gray-900 border border-purple-600 rounded px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-purple-500 transition-colors"
                          title="Query data as it was at this time"
                        />
                        {timeTravelTimestamp && (
                          <button
                            onClick={() => setTimeTravelTimestamp(null)}
                            className="text-purple-400 hover:text-purple-300 transition-colors"
                            title="Clear time travel"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="bg-green-900 bg-opacity-30 border border-green-700 rounded-lg px-4 py-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-green-400">Live</span>
                </div>
              </div>
            </div>
          </div>

          {/* Date Range Picker */}
          <DateRangePicker value={timeRange} onChange={setTimeRange} />
        </header>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-700">
            <nav className="flex gap-8">
              <button
                onClick={() => setActiveTab('metrics')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'metrics'
                    ? 'border-blue-500 text-blue-500'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                Dashboard Metrics
              </button>
              <button
                onClick={() => setActiveTab('chat')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'chat'
                    ? 'border-blue-500 text-blue-500'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                Natural Language Query
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <main>
          {activeTab === 'metrics' && (
            <DashboardMetrics
              timeRange={timeRange}
              apiBaseUrl={API_BASE_URL}
              project={projectSlug}
              mode={analyticsMode}
              timeTravelTimestamp={timeTravelTimestamp}
            />
          )}
          {activeTab === 'chat' && (
            <ChatInterface
              timeRange={timeRange}
              apiBaseUrl={API_BASE_URL}
              project={projectSlug}
              mode={analyticsMode}
              timeTravelTimestamp={timeTravelTimestamp}
            />
          )}
        </main>

        {/* Footer */}
        <footer className="mt-12 text-center text-sm text-gray-500">
          <p>
            Data sourced from {projectConfig.analytics?.athenaTable || 'Analytics'} (Athena + S3 Parquet) | Powered by AWS Bedrock
          </p>
        </footer>
      </div>

      {/* Mode Explainer Modal - PVQ only */}
      {projectSlug === 'pvq' && (
        <ModeExplainerModal
          isOpen={showModeExplainer}
          onClose={() => setShowModeExplainer(false)}
        />
      )}
    </div>
  );
};

export default ExecutiveDashboard;
